import { RuleViolationError } from '../errors.js';

export type MessageKind = 'text' | 'system' | 'file' | 'voice';
export type AttachmentMessageKind = 'file' | 'voice';

export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const VOICE_MAX_BYTES = 10 * 1024 * 1024;
export const VOICE_MAX_MS = 5 * 60_000;
export const FILE_NAME_MAX_CHARS = 200;

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const VOICE_TYPES = new Set([
  'audio/webm',
  'audio/ogg',
  'audio/mp4',
  'audio/mpeg',
  'audio/aac',
  'audio/wav',
  'audio/x-m4a',
]);

export interface AttachmentUpload {
  kind: AttachmentMessageKind;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  durationMs?: number | null;
}

/** `audio/webm;codecs=opus` → `audio/webm`; the parameters never decide anything. */
export function baseContentType(raw: string): string {
  const base = raw.split(';')[0]?.trim().toLowerCase() ?? '';
  return base.length > 0 ? base : 'application/octet-stream';
}

export function isImageContentType(contentType: string): boolean {
  return IMAGE_TYPES.has(baseContentType(contentType));
}

export function isVoiceContentType(contentType: string): boolean {
  return VOICE_TYPES.has(baseContentType(contentType));
}

/**
 * Only what `<img>` and `<audio>` need is served inline. Everything else,
 * SVG and HTML included, downloads, so a file can never run as a page.
 */
export function isInlineContentType(contentType: string): boolean {
  return isImageContentType(contentType) || isVoiceContentType(contentType);
}

export function normaliseFileName(raw: string | undefined | null): string {
  const leaf = (raw ?? '').split(/[\\/]/).pop() ?? '';
  // eslint-disable-next-line no-control-regex
  const clean = leaf.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim();
  if (clean.length === 0 || clean === '.' || clean === '..') return 'file';
  return clean.length > FILE_NAME_MAX_CHARS ? clean.slice(0, FILE_NAME_MAX_CHARS) : clean;
}

export function assertAttachmentUpload(upload: AttachmentUpload): void {
  if (!Number.isInteger(upload.sizeBytes) || upload.sizeBytes <= 0) {
    throw new RuleViolationError('chat.attachment_empty', 'The file is empty');
  }
  if (upload.kind === 'voice') {
    if (!isVoiceContentType(upload.contentType)) {
      throw new RuleViolationError('chat.voice_bad_type', 'A voice note must be an audio file', {
        contentType: upload.contentType,
      });
    }
    if (upload.sizeBytes > VOICE_MAX_BYTES) {
      throw new RuleViolationError('chat.voice_too_large', 'The voice note is too large', {
        max: VOICE_MAX_BYTES,
        sizeBytes: upload.sizeBytes,
      });
    }
    const duration = upload.durationMs;
    if (duration === undefined || duration === null || !Number.isInteger(duration) || duration <= 0) {
      throw new RuleViolationError('chat.voice_duration_required', 'A voice note needs its duration');
    }
    if (duration > VOICE_MAX_MS) {
      throw new RuleViolationError('chat.voice_too_long', 'Voice notes are limited to five minutes', {
        max: VOICE_MAX_MS,
        durationMs: duration,
      });
    }
    return;
  }
  if (upload.sizeBytes > ATTACHMENT_MAX_BYTES) {
    throw new RuleViolationError('chat.attachment_too_large', 'The file is too large', {
      max: ATTACHMENT_MAX_BYTES,
      sizeBytes: upload.sizeBytes,
    });
  }
}

export function kindCarriesAttachment(kind: MessageKind): kind is AttachmentMessageKind {
  return kind === 'file' || kind === 'voice';
}

/** A file or voice message always points at an attachment; text and system never do. */
export function assertKindMatchesAttachment(kind: MessageKind, attachmentId: string | null): void {
  const has = attachmentId !== null;
  if (kindCarriesAttachment(kind) !== has) {
    throw new RuleViolationError(
      'chat.kind_attachment_mismatch',
      has ? `A ${kind} message cannot carry an attachment` : `A ${kind} message needs an attachment`,
      { kind, attachmentId },
    );
  }
}
